import cn from 'classnames';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { SortStartHandler } from 'react-sortable-hoc';

import { Icon } from 'components';
import { Col, Row } from 'components/layout';
import { SortableItem, SortableList, Trigger } from 'components/Sortable';
import { InsightArticleRequest } from 'lib/models/insight';

import AddNewCard from '../AddNewCard';
import EditableCard from '../EditableCard';
import Gamification, { GamificationProps } from '../Gamification';
import SectionWrapper from '../SectionWrapper';

import styles from './ArticlesDetails.module.scss';

export interface ArticlesDetailsProps extends GamificationProps {
  showProgressBanner: boolean;
  sectionClass?: string;
}

export const ArticlesDetails: React.FC<ArticlesDetailsProps> = memo(
  ({
    showProgressBanner,
    progressDetails,
    profileCompletePercent,
    nonCompletedSection,
    handleProfileCompletionButton,
    handleNextButton,
    showNextButton,
    sectionClass,
  }: ArticlesDetailsProps) => {
    const { control, watch } = useFormContext();

    const { update, append, move } = useFieldArray({
      control,
      name: 'insights_attributes',
    });

    const [dragItemIndex, setDragItemIndex] = useState<number | null>(null);
    const [hoverItemIndex, setHoverItemIndex] = useState<number | null>(null);
    const [showProgressBannerState, setShowProgressBannerState] =
      React.useState<boolean>(showProgressBanner);

    useEffect(() => {
      if (showProgressBannerState === false)
        setShowProgressBannerState(showProgressBanner);
    }, [showProgressBanner]);

    const articles: InsightArticleRequest[] = watch('insights_attributes');

    const remove = (index: number) => {
      const updatedValues = articles.filter((item) => !item._destroy)[index];
      const indexToUpdate = articles.indexOf(updatedValues);
      updatedValues._destroy = true;
      update(indexToUpdate, updatedValues);
    };

    const onSortStart: SortStartHandler = ({ index }) => {
      setDragItemIndex(index);
    };
    const onSortEnd = ({
      oldIndex,
      newIndex,
    }: {
      oldIndex: number;
      newIndex: number;
    }): void => {
      setDragItemIndex(null);
      move(oldIndex, newIndex);
    };

    const sortableItemElement = useMemo(() => {
      return articles
        .filter((item) => !item._destroy)
        .map((item, index) => {
          const showControls =
            dragItemIndex === null
              ? hoverItemIndex === index
              : dragItemIndex === index;
          return (
            <SortableItem key={item.id || uuidv4()} index={index}>
              <EditableCard
                data={{ ...item }}
                isUrl={true}
                title={'Edit article'}
                index={index}
                onSave={update}
                onRemove={remove}
                onMouseEnter={() => setHoverItemIndex(index)}
                onMouseLeave={() => setHoverItemIndex(null)}
                key={index}
                trigger={
                  <Trigger className={styles.Trigger}>
                    <Icon iconName="drag-and-drop" size="xsmallplus" />
                  </Trigger>
                }
                isGrabbing={dragItemIndex === index}
                showControls={showControls}
              />
            </SortableItem>
          );
        });
    }, [articles, dragItemIndex]);

    return (
      <div id={`section_${progressDetails?.id}`} className={sectionClass}>
        {showProgressBannerState && (
          <Gamification
            profileCompletePercent={profileCompletePercent}
            nonCompletedSection={nonCompletedSection}
            progressDetails={progressDetails}
            handleProfileCompletionButton={() => {
              setShowProgressBannerState(false);
              handleProfileCompletionButton();
            }}
            handleNextButton={() => {
              setShowProgressBannerState(false);
              handleNextButton && handleNextButton();
            }}
            showNextButton={showNextButton}
          />
        )}
        <SectionWrapper
          title="Articles & news"
          description="Add up to five articles showcasing your company's recent accomplishments or milestones to help candidates write stronger applications."
          helperText="(Optional)"
          className={cn({ [styles.noBorderRadius]: showProgressBannerState })}
        >
          <Row>
            <Col span={24}>
              <SortableList
                lockAxis="y"
                lockToContainerEdges={true}
                useDragHandle={true}
                onSortStart={onSortStart}
                onSortEnd={onSortEnd}
              >
                {sortableItemElement}
              </SortableList>
              <AddNewCard
                isUrl={true}
                title={'Add article'}
                onSave={append}
                modalPlaceholders={{
                  title: 'Add article title',
                  subTitle: 'Paste article URL',
                }}
                itemCount={articles.filter((item) => !item._destroy).length}
                isArticle={true}
              />
            </Col>
          </Row>
        </SectionWrapper>
      </div>
    );
  }
);

ArticlesDetails.displayName = 'ArticlesDetails';
